import { useEffect, useState } from "react";
import { API_URL } from "../../helpers/flavor/app";
import { listPrivacyPolicyPublic } from "../../services/apiClient/settings/privacy";

export default function PrivacyPolicy() {
  const [privacyData, setPrivacyData] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("En");
  const [policyContent, setPolicyContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setLoading(true);
        const data = await listPrivacyPolicyPublic({
          page: 1,
          rowsPerPage: 1000,
        });
        if (data) {
          setPrivacyData(data);
          await fetchPolicyContent(data[`privacyPolicy${selectedLanguage}`]);
        }
      } catch (error) {
        setError("Failed to load privacy policy");
      } finally {
        setLoading(false);
      }
    };
    fetchPrivacyPolicy();
  }, []); // eslint-disable-line

  const fetchPolicyContent = async (url) => {
    if (!url) {
      setError("No content available for selected language");
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}${url}`);
      if (!response.ok) throw new Error("Failed to fetch policy content");
      const content = await response.text();
      setPolicyContent(content);
      setError(null);
    } catch (error) {
      setError("Failed to load policy content");
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (language) => {
    setSelectedLanguage(language);
    if (privacyData) {
      await fetchPolicyContent(privacyData[`privacyPolicy${language}`]);
    }
  };

  const styles = {
    container: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "2rem",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      lineHeight: 1.6,
      color: "#333",
    },
    languageSelector: {
      marginBottom: "2rem",
      display: "flex",
      justifyContent: "center",
      gap: 0,
      background: "none",
      borderRadius: "999px",
      overflow: "hidden",
      border: "1px solid #dee2e6",
      width: "fit-content",
      marginLeft: "auto",
      marginRight: "auto",
    },
    languageButton: {
      padding: "0.6rem 2.2rem",
      border: "none",
      background: "none",
      cursor: "pointer",
      fontSize: "1rem",
      fontWeight: "500",
      color: "#495057",
      transition: "background 0.2s, color 0.2s",
      outline: "none",
      borderRight: "1px solid #dee2e6",
    },
    firstLanguageButton: {
      borderTopLeftRadius: "999px",
      borderBottomLeftRadius: "999px",
    },
    lastLanguageButton: {
      borderTopRightRadius: "999px",
      borderBottomRightRadius: "999px",
      borderRight: "none",
    },
    activeLanguageButton: {
      background: "#007bff",
      color: "white",
    },
    loading: {
      textAlign: "center",
      padding: "2rem",
      color: "#666",
      fontSize: "1.1rem",
    },
    error: {
      color: "#dc3545",
      textAlign: "center",
      padding: "2rem",
      backgroundColor: "#fff5f5",
      borderRadius: "8px",
      margin: "1rem 0",
    },
    content: {
      backgroundColor: "white",
      padding: "2rem",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
  };

  if (loading && !policyContent) {
    return <div style={styles.loading}>Loading privacy policy...</div>;
  }
  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.languageSelector}>
        <button
          style={{
            ...styles.languageButton,
            ...styles.firstLanguageButton,
            ...(selectedLanguage === "En" && styles.activeLanguageButton),
          }}
          onClick={() => handleLanguageChange("En")}
        >
          English
        </button>
        <button
          style={{
            ...styles.languageButton,
            ...styles.lastLanguageButton,
            ...(selectedLanguage === "Kh" && styles.activeLanguageButton),
          }}
          onClick={() => handleLanguageChange("Kh")}
        >
          ខ្មែរ
        </button>
      </div>
      <div style={styles.content}>
        <style>{`
          .policy-doc h1 {
            font-size: 2.2rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: #222;
            font-family: inherit;
          }
          .policy-doc .policy-date {
            font-size: 1rem;
            color: #666;
            margin-bottom: 2rem;
            font-style: italic;
          }
          .policy-doc h2 {
            font-size: 1.3rem;
            font-weight: 600;
            margin-top: 2rem;
            margin-bottom: 1rem;
            color: #222;
            border-bottom: 1px solid #eee;
            padding-bottom: 0.3rem;
          }
          .policy-doc h3 {
            font-size: 1.1rem;
            font-weight: 600;
            margin-top: 1.2rem;
            margin-bottom: 0.5rem;
            color: #222;
          }
          .policy-doc p {
            font-size: 1.05rem;
            color: #222;
            margin-bottom: 1rem;
            line-height: 1.7;
          }
          .policy-doc ul, .policy-doc ol {
            margin-left: 2rem;
            margin-bottom: 1rem;
          }
          .policy-doc li {
            font-size: 1.05rem;
            color: #222;
            margin-bottom: 0.5rem;
            line-height: 1.6;
          }
          .policy-doc b, .policy-doc strong {
            font-weight: 600;
            color: #111;
          }
          .policy-doc {
            font-family: 'Segoe UI', Arial, sans-serif;
            color: #222;
          }
          .policy-doc a {
            color: #007bff;
            text-decoration: underline;
            word-break: break-all;
            transition: color 0.2s;
          }
          .policy-doc a:hover, .policy-doc a:focus {
            color: #0056b3;
          }
        `}</style>
        {loading ? <div style={styles.loading}>Loading content...</div> : <div className="policy-doc" dangerouslySetInnerHTML={{ __html: policyContent }} />}
      </div>
    </div>
  );
}
